/**
 * Storage for the NEW dynamic commission rules.
 *
 * These use their own collections (dynamicCommissionRules, ...). The live
 * commission engine reads the old `commissionRules` collection, which has a
 * different shape. Nothing here writes to that collection, so live payouts are
 * not affected by anything in this file.
 *
 * Every save writes three records in one batch, so they stand or fall together:
 *   1. the rule itself (current version),
 *   2. a version snapshot (kept forever, never overwritten),
 *   3. an audit entry (who, when, before and after).
 */
import { collection, doc, getDocs, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';
import {
  DynamicCommissionRule,
  validateRule,
} from '../utils/dynamicCommission/dynamicCommissionEngine';

export const DYNAMIC_RULES = 'dynamicCommissionRules';
export const DYNAMIC_RULE_VERSIONS = 'dynamicCommissionRuleVersions';
export const DYNAMIC_AUDIT = 'dynamicCommissionAuditLogs';

export interface RuleActor {
  id: string;
  name: string;
}

/** Firestore rejects `undefined` values, so drop them before writing. */
const clean = <T,>(value: T): T => JSON.parse(JSON.stringify(value));

/** One read of the rules collection. Called only when an administrator opens the screen. */
export async function fetchDynamicRules(): Promise<DynamicCommissionRule[]> {
  const snap = await getDocs(collection(db, DYNAMIC_RULES));
  return snap.docs.map((d) => ({ ...(d.data() as DynamicCommissionRule), id: d.id }));
}

const newAuditId = (ruleId: string): string =>
  `${Date.now().toString(36)}_${ruleId}_${Math.random().toString(36).slice(2, 8)}`;

/**
 * Saves a new or edited rule. Refuses to save if validation fails.
 * `existing` is the stored rule before this change (null for a new rule).
 */
export async function saveDynamicRule(
  rule: DynamicCommissionRule,
  existing: DynamicCommissionRule | null,
  actor: RuleActor,
): Promise<DynamicCommissionRule> {
  const errors = validateRule(rule);
  if (errors.length > 0) throw new Error(errors.join(' '));

  const now = new Date().toISOString();
  const version = existing ? existing.version + 1 : 1;
  const saved = clean<DynamicCommissionRule>({ ...rule, version });
  const savedWithMeta = clean({
    ...saved,
    updatedAt: now,
    updatedBy: actor.id,
    updatedByName: actor.name,
  });

  const batch = writeBatch(db);
  batch.set(doc(db, DYNAMIC_RULES, rule.id), savedWithMeta);
  batch.set(
    doc(db, DYNAMIC_RULE_VERSIONS, `${rule.id}_v${version}`),
    clean({ ruleId: rule.id, version, snapshot: savedWithMeta, savedAt: now, savedBy: actor.id, savedByName: actor.name }),
  );
  batch.set(
    doc(db, DYNAMIC_AUDIT, newAuditId(rule.id)),
    clean({
      action: !existing
        ? 'rule_created'
        : existing.active && !rule.active
          ? 'rule_deactivated'
          : 'rule_edited',
      ruleId: rule.id,
      version,
      before: existing ? clean(existing) : null,
      after: savedWithMeta,
      actorId: actor.id,
      actorName: actor.name,
      at: now,
    }),
  );
  await batch.commit();
  return savedWithMeta;
}

/**
 * Deactivates a rule. The rule is kept (never deleted), so old references stay valid.
 */
export async function deactivateDynamicRule(
  existing: DynamicCommissionRule,
  actor: RuleActor,
): Promise<DynamicCommissionRule> {
  return saveDynamicRule({ ...existing, active: false }, existing, actor);
}
